/**
 * Description: API service layer with axios client and all API endpoints
 */

import axios, { AxiosInstance, AxiosError } from 'axios'

// Use the current page's origin for API calls so any domain pointing at the
// Front Door (dev-dal.ni.comcast.com, *.azurefd.net) calls its own /api and
// stays same-origin. Localhost dev (http://localhost:3000) falls back to the
// build-time env var because the backend runs on a different port (5001).
export const API_BASE_URL =
  typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? `${window.location.origin}/api`
    : import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api'

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // always send session cookie
})

// setAccessToken is kept as a no-op for any remaining call sites during migration.
// It will be removed once all callers are confirmed clean.
export const setAccessToken = (_token: string | null): void => {
  /* no-op: BFF session cookie is used */
}

// Active context id — set by UserContextProvider, sent as X-Active-Context header
let _activeContextId: number | null = null
export const setActiveContextId = (id: number | null) => {
  _activeContextId = id
}

// Public-by-design endpoints under the API access registration flow. These
// MUST NOT trigger an Entra login redirect — visitors registering for docs
// access don't have a Comcast tenant identity yet. Anything matching this
// regex skips the MSAL token interceptor entirely.
const PUBLIC_PATH_RE = /^\/access-request(\/|$)/

// Request interceptor — attach active context header; session cookie is sent automatically.
apiClient.interceptors.request.use(
  async (config) => {
    if (_activeContextId !== null) {
      config.headers['X-Active-Context'] = String(_activeContextId)
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor — handle session expiry 401s.
//
// When the backend returns 401, the session has expired or been invalidated.
// Clear any residual MSAL state and redirect to /login so the user can
// re-authenticate via Entra. The hard reload also drops stale React state.
let sessionExpiryHandling = false

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status
    const url = error.config?.url ?? ''

    if (status === 401 && !PUBLIC_PATH_RE.test(url) && !sessionExpiryHandling) {
      sessionExpiryHandling = true
      // MSAL cache is already empty (cleared after session exchange).
      // Just redirect to login; no MSAL cleanup needed.
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.replace('/login?expired=1')
      }
    }
    return Promise.reject(error)
  },
)

export interface SystemComplexity {
  id?: number
  system_id?: number
  code_owned?: boolean
  code_access?: boolean
  code_owner?: string
  api_documentation?: string
  product_overlap?: number
  complexity_score?: number
}

export interface System {
  id?: number
  system_name: string
  description?: string
  origin?: string
  acquisition_type?: 'acquisition' | 'homegrown' | 'other'
  custom_type?: 'custom' | 'vendor_partnership' | 'off_the_shelf' | 'other'
  user_interfaces?: string
  maintenance_schedule?: string
  business_owner?: string
  technical_product_owner?: string
  management_type?: 'BIT' | 'CB' | 'shared' | 'other'
  sales_channels?: string
  customers_impacted?: string
  monthly_volume?: string
  customer_footprint?: string
  product_related_notes?: string
  other_user_groups?: string
  site_capability?: string
  geographic_scope?: 'international' | 'domestic' | 'both' | 'other'
  limitations?: string
  integration_capabilities?: string
  dependencies?: string
  impacted_subsystems?: string
  northstar_alignment?: string
  shared_resources_non_cb?: boolean
  security_score?: 'low' | 'medium' | 'high' | 'critical'
  compliance_score?: 'low' | 'medium' | 'high' | 'critical'
  created_at?: string
  updated_at?: string
  created_by?: string
  updated_by?: string
  product_count?: number
  complexity?: Partial<SystemComplexity>
}

export interface Product {
  id?: number
  product_name: string
  description?: string
  product_code?: string
  category?: string
  status?: 'active' | 'inactive' | 'deprecated'
  systemIds?: number[]
  created_at?: string
  updated_at?: string
  created_by?: string
  updated_by?: string
}

export interface Capability {
  id?: number
  capability_name: string
  description?: string
  capability_code?: string
  category?: string
  status?: 'active' | 'inactive' | 'deprecated'
  systemIds?: number[]
  created_at?: string
  updated_at?: string
  created_by?: string
  updated_by?: string
}

export interface UserBizContext {
  id: number
  name: string
  display_name: string
  description?: string
}

export interface User {
  oid: string
  email: string
  name: string
  role: string
  contexts?: UserBizContext[]
}

export interface ComplexityHistoryPoint {
  date: string
  avg_score: number
  system_count: number
}

/** A queued/running/finished Data Digest job, as shown in the jobs panel. */
export interface DigestJob {
  jobId: string
  label: string | null
  itemCount: number
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
  total: number
  completed: number
  currentFile?: string | null
  error?: string
  /** 1-based place in line while pending; 0 once running; null if not queued here. */
  queuePosition?: number | null
  startedAt?: string | null
  finishedAt?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface ChatResponse {
  message: string
  timestamp: string
}

export interface ChatStatus {
  available: boolean
  testAssistantAvailable?: boolean
  message: string
}

export interface ChatThread {
  id: number
  user_email: string
  context_id: number
  title: string
  status: 'active' | 'archived' | 'deleted'
  created_at: string
  updated_at: string
  last_message_at: string | null
  // Only populated by listChatThreads, which lists threads across all of the caller's contexts.
  context_name?: string
  context_display_name?: string
}

export interface ChatThreadMessage {
  id: number
  thread_id: number
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  created_at: string
  metadata: Record<string, unknown> | null
}

export interface AdminContext {
  id: number
  name: string
  display_name: string
  description?: string
  entra_group_id?: string | null
  preferred_skill_id?: number | null
  skill_exclusive?: boolean
  skill_context_description?: string | null
  is_active: boolean
}

export interface AdminSkill {
  id: number
  skill_key: string
  name: string
  description: string
  tags: string[]
  is_active: boolean
}

export interface ContextMember {
  oid: string
  email: string
  display_name: string
  source: 'manual' | 'entra_sync'
  created_at: string
}

export type TopologyPluginScope = 'global' | 'service' | 'route'
export type TopologyCategory = 'internal' | 'external' | 'auth' | 'health'

export interface TopologyPlugin {
  name: string
  scope: TopologyPluginScope
  summary: string
}

export interface TopologyRoute {
  id: string
  routeName: string
  paths: string[]
  methods: string[]
  headerMatch?: { header: string; value: string }
  protocols: string[]
  stripPath: boolean
  service: string
  upstreamUrl: string
  plugins: TopologyPlugin[]
  category: TopologyCategory
  tags: string[]
}

export interface TopologyResponse {
  routes: TopologyRoute[]
  fetchedAt: string
}

export interface UpstreamTargetHealth {
  upstream: string
  target: string
  state: string
  weight: number
}

export interface LatencyBucket {
  le: number
  count: number
}

export interface RouteMetrics {
  route: string
  service: string
  requests: number
  requests2xx: number
  requests4xx: number
  requests429: number
  requests5xx: number
  meanMs: number
  p50Ms: number
  p95Ms: number
  p99Ms: number
  upstreamMeanMs: number
  upstreamP50Ms: number
  upstreamP95Ms: number
  upstreamP99Ms: number
  kongMeanMs: number
  kongP50Ms: number
  kongP95Ms: number
  kongP99Ms: number
  bandwidthIngressBytes: number
  bandwidthEgressBytes: number
  requestBuckets: LatencyBucket[]
  kongBuckets: LatencyBucket[]
}

export interface KongHealthResponse {
  fetchedAt: string
  kongVersion: string
  status: {
    database: 'reachable' | 'unreachable' | 'unknown'
    memoryLuaUsedKb: number
    workerVmMb: number
    workerCount: number
    pid: number
    connectionsActive: number
    connectionsTotal: number
    connectionsReading: number
    connectionsWriting: number
    connectionsWaiting: number
  }
  totals: {
    requests: number
    requests2xx: number
    requests4xx: number
    requests429: number
    requests5xx: number
    bandwidthIngressBytes: number
    bandwidthEgressBytes: number
    meanMs: number
    p50Ms: number
    p95Ms: number
    p99Ms: number
    upstreamMeanMs: number
    upstreamP50Ms: number
    upstreamP95Ms: number
    upstreamP99Ms: number
    kongMeanMs: number
    kongP50Ms: number
    kongP95Ms: number
    kongP99Ms: number
  }
  routes: RouteMetrics[]
  upstreams: UpstreamTargetHealth[]
}

// ── FinOps cost dashboard types ─────────────────────────────────────────────
export interface FinopsNamedCost {
  label: string
  cost: number
  tokens?: number
}
export interface FinopsModelCost {
  model: string
  calls: number
  inputTokens: number
  outputTokens: number
  cost: number
}
export interface FinopsLlmReport {
  totalCost: number
  totalCalls: number
  totalInputTokens: number
  totalOutputTokens: number
  byModel: FinopsModelCost[]
  byContext: FinopsNamedCost[]
  byUser: FinopsNamedCost[]
  daily: Array<{ date: string; cost: number }>
}
export interface FinopsAzureReport {
  available: boolean
  currency: string
  totalCost: number
  byService: FinopsNamedCost[]
  timeframe: string
  asOf: string
  error?: string
}
export interface FinopsSummary {
  periodDays: number
  llm: FinopsLlmReport
  azure: FinopsAzureReport
  pricingNote: string
}

// ── Partner Bruno test-results types ────────────────────────────────
export type AssertionStatus = 'pass' | 'fail' | 'error' | 'skip'

export interface PartnerTestRun {
  id: number
  collection_name: string
  environment: string | null
  total_tests: number
  passed: number
  failed: number
  errored: number
  pass_rate: number
  duration_ms: number
  triggered_by: string | null
  created_at: string
}

export interface PartnerTestResult {
  id: number
  run_id: number
  request_name: string
  folder_path: string | null
  method: string | null
  url: string | null
  status_code: number | null
  assertion_status: AssertionStatus
  assertions_passed: number
  assertions_failed: number
  duration_ms: number
  error_message: string | null
  created_at: string
}

export interface PartnerTestRunDetail extends PartnerTestRun {
  results: PartnerTestResult[]
}

// ── LMaaS metro footprint (CPQ Indirect Partners) ────────────────────

export interface LmaasMetro {
  code: string
  name: string
  enabled: boolean
  zipCount: number
}

// API methods
export const api = {
  // Auth
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get('/auth/me')
    return response.data.data
  },

  // Systems
  getAllSystems: async (): Promise<System[]> => {
    const response = await apiClient.get('/systems')
    return response.data.data
  },

  getSystemById: async (id: number): Promise<System> => {
    const response = await apiClient.get(`/systems/${id}`)
    return response.data.data
  },

  createSystem: async (system: System): Promise<{ id: number }> => {
    const response = await apiClient.post('/systems', system)
    return response.data.data
  },

  updateSystem: async (id: number, system: Partial<System>): Promise<void> => {
    await apiClient.put(`/systems/${id}`, system)
  },

  deleteSystem: async (id: number): Promise<void> => {
    await apiClient.delete(`/systems/${id}`)
  },

  cloneSystem: async (id: number): Promise<{ id: number }> => {
    const response = await apiClient.post(`/systems/${id}/clone`)
    return response.data.data
  },

  searchSystems: async (query: string): Promise<System[]> => {
    const response = await apiClient.get(`/systems/search?q=${encodeURIComponent(query)}`)
    return response.data.data
  },

  getAuditHistory: async (systemId: number): Promise<any[]> => {
    const response = await apiClient.get(`/systems/${systemId}/audit`)
    return response.data.data
  },

  getComplexityHistory: async (): Promise<ComplexityHistoryPoint[]> => {
    const response = await apiClient.get('/systems/complexity/history')
    return response.data.data
  },

  getSystemProducts: async (systemId: number): Promise<Product[]> => {
    const response = await apiClient.get(`/systems/${systemId}/products`)
    return response.data.data
  },

  linkProductToSystem: async (systemId: number, productId: number): Promise<void> => {
    await apiClient.post(`/systems/${systemId}/products/${productId}`)
  },

  unlinkProductFromSystem: async (systemId: number, productId: number): Promise<void> => {
    await apiClient.delete(`/systems/${systemId}/products/${productId}`)
  },

  // Products
  getAllProducts: async (): Promise<Product[]> => {
    const response = await apiClient.get('/products')
    return response.data.data
  },

  getProductById: async (id: number): Promise<Product> => {
    const response = await apiClient.get(`/products/${id}`)
    return response.data.data
  },

  createProduct: async (product: Product): Promise<{ id: number }> => {
    const response = await apiClient.post('/products', product)
    return response.data.data
  },

  updateProduct: async (id: number, product: Partial<Product>): Promise<void> => {
    await apiClient.put(`/products/${id}`, product)
  },

  deleteProduct: async (id: number): Promise<void> => {
    await apiClient.delete(`/products/${id}`)
  },

  searchProducts: async (query: string): Promise<Product[]> => {
    const response = await apiClient.get(`/products/search?q=${encodeURIComponent(query)}`)
    return response.data.data
  },

  getProductSystems: async (productId: number): Promise<System[]> => {
    const response = await apiClient.get(`/products/${productId}/systems`)
    return response.data.data
  },

  // Capabilities (Service Assurance)
  getAllCapabilities: async (): Promise<Capability[]> => {
    const response = await apiClient.get('/capabilities')
    return response.data.data
  },

  getCapabilityById: async (id: number): Promise<Capability> => {
    const response = await apiClient.get(`/capabilities/${id}`)
    return response.data.data
  },

  createCapability: async (capability: Capability): Promise<{ id: number }> => {
    const response = await apiClient.post('/capabilities', capability)
    return response.data.data
  },

  updateCapability: async (id: number, capability: Partial<Capability>): Promise<void> => {
    await apiClient.put(`/capabilities/${id}`, capability)
  },

  deleteCapability: async (id: number): Promise<void> => {
    await apiClient.delete(`/capabilities/${id}`)
  },

  searchCapabilities: async (query: string): Promise<Capability[]> => {
    const response = await apiClient.get(`/capabilities/search?q=${encodeURIComponent(query)}`)
    return response.data.data
  },

  getCapabilitySystems: async (capabilityId: number): Promise<System[]> => {
    const response = await apiClient.get(`/capabilities/${capabilityId}/systems`)
    return response.data.data
  },

  getSystemCapabilities: async (systemId: number): Promise<Capability[]> => {
    const response = await apiClient.get(`/systems/${systemId}/capabilities`)
    return response.data.data
  },

  linkCapabilityToSystem: async (systemId: number, capabilityId: number): Promise<void> => {
    await apiClient.post(`/systems/${systemId}/capabilities/${capabilityId}`)
  },

  unlinkCapabilityFromSystem: async (systemId: number, capabilityId: number): Promise<void> => {
    await apiClient.delete(`/systems/${systemId}/capabilities/${capabilityId}`)
  },

  // Chatbot
  sendChatMessage: async (messages: ChatMessage[], includeContext: boolean = true): Promise<ChatResponse> => {
    const response = await apiClient.post('/chat', { messages, includeContext })
    return response.data
  },

  createChatThread: async (title?: string): Promise<ChatThread> => {
    const response = await apiClient.post('/chat/threads', title ? { title } : {})
    return response.data.thread
  },

  listChatThreads: async (search?: string): Promise<ChatThread[]> => {
    const response = await apiClient.get('/chat/threads', { params: search ? { q: search } : undefined })
    return response.data.threads
  },

  getChatThread: async (id: number): Promise<{ thread: ChatThread; messages: ChatThreadMessage[] }> => {
    const response = await apiClient.get(`/chat/threads/${id}`)
    return response.data
  },

  renameChatThread: async (id: number, title: string): Promise<void> => {
    await apiClient.patch(`/chat/threads/${id}`, { title })
  },

  archiveChatThread: async (id: number): Promise<void> => {
    await apiClient.patch(`/chat/threads/${id}`, { status: 'archived' })
  },

  sendThreadMessage: async (
    id: number,
    content: string,
    includeContext: boolean = true,
  ): Promise<{ userMessage: ChatThreadMessage; assistantMessage: ChatThreadMessage }> => {
    const response = await apiClient.post(`/chat/threads/${id}/messages`, { content, includeContext })
    return response.data
  },

  getChatStatus: async (): Promise<ChatStatus> => {
    const response = await apiClient.get('/chat/status')
    return response.data
  },

  // AI Test Assistant — optional, separate session/endpoint gated by backend role/allow-list
  sendTestChatMessage: async (messages: ChatMessage[], includeContext: boolean = true): Promise<ChatResponse> => {
    const response = await apiClient.post('/chat/test', { messages, includeContext })
    return response.data
  },

  getDatabaseSchema: async (): Promise<any> => {
    const response = await apiClient.get('/chat/schema')
    return response.data
  },

  // Context Digester - SharePoint/OneDrive file browsing and digestion

  // Browse user's OneDrive files
  browseOneDriveFiles: async (folderId?: string): Promise<{ files: any[] }> => {
    const response = await apiClient.get('/context-digester/browse', {
      params: { folderId },
    })
    return response.data.data
  },

  // Browse files shared with the user
  browseSharedFiles: async (): Promise<{ files: any[] }> => {
    const response = await apiClient.get('/context-digester/shared')
    return response.data.data
  },

  // Browse inside a shared folder
  browseSharedFolder: async (driveId: string, folderId: string): Promise<{ files: any[] }> => {
    const response = await apiClient.get('/context-digester/shared/folder', {
      params: { driveId, folderId },
    })
    return response.data.data
  },

  // Get SharePoint sites — by search term, or resolve a specific site by URL.
  // URL resolution works where tenant-wide search is blocked by SharePoint governance.
  getSharePointSites: async (searchQuery?: string, siteUrl?: string): Promise<{ sites: any[] }> => {
    const response = await apiClient.get('/context-digester/sharepoint/sites', {
      params: { search: searchQuery, url: siteUrl },
    })
    return response.data.data
  },

  // Get document libraries for a SharePoint site
  getSiteDocumentLibraries: async (siteId: string): Promise<{ libraries: any[] }> => {
    const response = await apiClient.get(`/context-digester/sharepoint/site/${siteId}/libraries`)
    return response.data.data
  },

  // Browse SharePoint library
  browseSharePointLibrary: async (driveId: string, folderId?: string): Promise<{ files: any[] }> => {
    const response = await apiClient.get(`/context-digester/sharepoint/library/${driveId}`, {
      params: { folderId },
    })
    return response.data.data
  },

  // Legacy alias for backward compatibility
  browseSharePointFiles: async (folderId?: string): Promise<{ files: any[] }> => {
    const response = await apiClient.get('/context-digester/browse', {
      params: { folderId },
    })
    return response.data.data
  },

  // Digest selected files. The backend runs the digest as an async job and we
  // poll its status so the UI can show real progress (completed/total + the file
  // currently being processed).
  // Queue a digest job. Returns as soon as the job is accepted — large selections
  // take far longer than any gateway timeout, so the UI watches the jobs list
  // instead of holding the request open.
  digestFiles: async (
    items: Array<{ id: string; driveId?: string; source?: string; folderPath?: string }>,
    label?: string,
  ): Promise<{ jobId: string; label: string; queuePosition: number }> => {
    const response = await apiClient.post('/context-digester/digest', { items, label })
    return response.data.data
  },

  // All digest jobs for the current user in the active context, newest first.
  getDigestJobs: async (
    limit = 50,
  ): Promise<{
    jobs: DigestJob[]
    queue: { running: number; waiting: number; maxConcurrent: number }
  }> => {
    const response = await apiClient.get('/context-digester/jobs', { params: { limit } })
    return response.data.data
  },

  // Single job progress (used after a job finishes to pull its per-file results).
  getDigestJobStatus: async (jobId: string): Promise<DigestJob & { results?: any[] }> => {
    const response = await apiClient.get(`/context-digester/digest/status/${jobId}`)
    return response.data.data
  },

  // Cancel a queued/running job, or drop a finished one from the list.
  // POST, not DELETE: DELETE would require a `context_digester:delete` permission
  // that no role can hold, so cancelling would 403 for everyone.
  cancelDigestJob: async (jobId: string): Promise<{ jobId: string; action: 'cancelled' | 'removed' }> => {
    const response = await apiClient.post(`/context-digester/jobs/${jobId}/cancel`)
    return response.data.data
  },

  // Remove every completed/failed/cancelled job from the list.
  clearFinishedDigestJobs: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/context-digester/jobs/clear-finished')
    return response.data.data
  },

  // Get user's stored/digested documents
  getStoredDocuments: async (): Promise<{ documents: any[]; count: number }> => {
    const response = await apiClient.get('/context-digester/documents')
    return response.data.data
  },

  // Delete a specific stored document
  deleteStoredDocument: async (id: number): Promise<void> => {
    await apiClient.delete(`/context-digester/documents/${id}`)
  },

  // Delete all user's stored documents
  deleteAllStoredDocuments: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.delete('/context-digester/documents')
    return response.data
  },

  // Search stored documents
  searchStoredDocuments: async (query: string, limit: number = 5): Promise<{ results: any[] }> => {
    const response = await apiClient.post('/context-digester/documents/search', { query, limit })
    return response.data.data
  },

  // Purge all digested documents (context index)
  purgeContextIndex: async (): Promise<{ deletedCount: number }> => {
    const response = await apiClient.post('/context-digester/purge')
    return response.data.data
  },

  // Superadmin: wipe ALL digested documents, vectors, and job state for the
  // active context (across every user). Uses the X-Active-Context header.
  purgeContextDomain: async (): Promise<{
    contextId: number
    documents: number
    vectorChunks: number
    jobs: number
  }> => {
    const response = await apiClient.post('/context-digester/admin/purge-context')
    return response.data.data
  },

  // Reindex systems and products into digested documents for LLM context
  reindexContext: async (): Promise<{ indexed: number; systems: number; products: number }> => {
    const response = await apiClient.post('/context-digester/reindex')
    return response.data.data
  },

  // Context Admin
  adminGetContexts: async (): Promise<AdminContext[]> => {
    const response = await apiClient.get('/contextAdmin/contexts')
    return response.data.data
  },

  adminUpdateEntraGroupId: async (contextId: number, entraGroupId: string | null): Promise<AdminContext> => {
    const response = await apiClient.patch(`/contextAdmin/contexts/${contextId}`, { entra_group_id: entraGroupId })
    return response.data.data
  },

  adminGetSkills: async (): Promise<AdminSkill[]> => {
    const response = await apiClient.get('/contextAdmin/skills')
    return response.data.data
  },

  adminUpdateContextSkill: async (
    contextId: number,
    params: { preferred_skill_id: number | null; skill_exclusive: boolean; skill_context_description: string | null },
  ): Promise<AdminContext> => {
    const response = await apiClient.patch(`/contextAdmin/contexts/${contextId}/skill`, params)
    return response.data.data
  },

  adminGetMembers: async (contextId: number): Promise<ContextMember[]> => {
    const response = await apiClient.get(`/contextAdmin/contexts/${contextId}/members`)
    return response.data.data
  },

  adminAddMember: async (contextId: number, userOid: string): Promise<void> => {
    await apiClient.post(`/contextAdmin/contexts/${contextId}/members`, { userOid })
  },

  adminRemoveMember: async (contextId: number, userOid: string): Promise<void> => {
    await apiClient.delete(`/contextAdmin/contexts/${contextId}/members/${encodeURIComponent(userOid)}`)
  },

  adminGetKongTopology: async (): Promise<TopologyResponse> => {
    const response = await apiClient.get('/contextAdmin/kong/topology')
    return response.data.data
  },

  adminGetKongHealth: async (): Promise<KongHealthResponse> => {
    const response = await apiClient.get('/contextAdmin/kong/health')
    return response.data.data
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getIntegrationStats: async (): Promise<any> => {
    const response = await apiClient.get('/contextAdmin/integration-stats')
    return response.data.data
  },

  // ── FinOps cost dashboard (admin context) ───────────────────────────────────
  getFinopsSummary: async (days = 30): Promise<FinopsSummary> => {
    const response = await apiClient.get(`/finops/summary?days=${days}`)
    return response.data.data
  },

  // ── External-API access requests ────────────────────────────────────────────
  //
  // Public flow:
  //   1) /api-access/register page calls accessRequestRegister()
  //   2) admin approves in /admin/api-access-requests, copies code, emails it
  //   3) requester visits /api-access/login and calls accessRequestRedeem()
  //   4) redeem sets an httpOnly cookie; browser navigation to
  //      /api/docs/external/ then succeeds.
  accessRequestRegister: async (payload: {
    email: string
    first_name: string
    last_name: string
    company: string
    use_case?: string
    request_type?: 'documentation' | 'access'
  }): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post('/access-request/register', payload)
    return response.data
  },

  accessRequestRequestOtp: async (email: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post('/access-request/otp/request', { email })
    return response.data
  },

  accessRequestRedeem: async (email: string, code: string): Promise<{ success: boolean; redirect?: string }> => {
    const response = await apiClient.post('/access-request/redeem', { email, code }, { withCredentials: true })
    return response.data
  },

  accessRequestMe: async (): Promise<{
    email: string
    first_name: string
    verified_at: string | null
    totp_enrolled: boolean
    request_type: 'documentation' | 'access'
  }> => {
    const response = await apiClient.get('/access-request/me', { withCredentials: true })
    return response.data.data
  },

  accessRequestTotpEnroll: async (): Promise<{ otpauth_url: string; qr_data_url: string }> => {
    // Body must be an object (not null) — Express body-parser strict mode
    // rejects bare-primitive JSON and would 400 before the route handler runs.
    const response = await apiClient.post('/access-request/totp/enroll', {}, { withCredentials: true })
    return response.data.data
  },

  accessRequestTotpConfirm: async (code: string): Promise<{ success: boolean; redirect?: string }> => {
    const response = await apiClient.post('/access-request/totp/confirm', { code }, { withCredentials: true })
    return response.data
  },

  // Admin (superadmin context-gated) ─────────────────────────────────────────
  adminListAccessRequests: async (
    opts: { status?: 'pending' | 'approved' | 'rejected' | 'activity'; type?: 'documentation' | 'access' } = {},
  ): Promise<AccessRequest[]> => {
    const params: Record<string, string> = {}
    if (opts.status) params.status = opts.status
    if (opts.type) params.type = opts.type
    const response = await apiClient.get('/access-request/admin', { params })
    return response.data.data
  },

  adminApproveAccessRequest: async (
    id: string,
  ): Promise<{ id: string; code_sent: boolean; expires_in_minutes: number }> => {
    const response = await apiClient.post(`/access-request/admin/${id}/approve`)
    return response.data.data
  },

  adminRejectAccessRequest: async (id: string, reason?: string): Promise<void> => {
    await apiClient.post(`/access-request/admin/${id}/reject`, { reason })
  },

  // ── Partner Bruno test-results (superadmin) ──────────────────────────
  getPartnerTestLatestRuns: async (): Promise<PartnerTestRun[]> => {
    const response = await apiClient.get('/admin/test-results/bruno/partners')
    return response.data.data
  },

  getPartnerTestRunHistory: async (collectionName: string, limit = 50): Promise<PartnerTestRun[]> => {
    const response = await apiClient.get(
      `/admin/test-results/bruno/partners/${encodeURIComponent(collectionName)}/history?limit=${limit}`,
    )
    return response.data.data
  },

  getPartnerTestRunDetail: async (runId: number): Promise<PartnerTestRunDetail> => {
    const response = await apiClient.get(`/admin/test-results/bruno/partners/runs/${runId}`)
    return response.data.data
  },

  // ── LMaaS metro footprint (CPQ Indirect Partners) ──────────────────
  getLmaasMetros: async (): Promise<LmaasMetro[]> => {
    const response = await apiClient.get('/admin/lmaas-metros')
    return response.data
  },

  createLmaasMetro: async (metro: { code: string; name: string; enabled?: boolean }): Promise<LmaasMetro> => {
    const response = await apiClient.post('/admin/lmaas-metros', metro)
    return response.data
  },

  updateLmaasMetro: async (code: string, changes: { name?: string; enabled?: boolean }): Promise<LmaasMetro> => {
    const response = await apiClient.patch(`/admin/lmaas-metros/${encodeURIComponent(code)}`, changes)
    return response.data
  },

  deleteLmaasMetro: async (code: string): Promise<void> => {
    await apiClient.delete(`/admin/lmaas-metros/${encodeURIComponent(code)}`)
  },

  getLmaasMetroPostcodes: async (code: string): Promise<string[]> => {
    const response = await apiClient.get(`/admin/lmaas-metros/${encodeURIComponent(code)}/postcodes`)
    return response.data.postcodes
  },

  uploadLmaasMetroPostcodes: async (code: string, file: File): Promise<{ code: string; zipCount: number }> => {
    const form = new FormData()
    form.append('file', file)
    // Clear the instance default JSON Content-Type so axios detects the FormData
    // and sets multipart/form-data with a boundary — otherwise multer sees no file.
    const response = await apiClient.put(`/admin/lmaas-metros/${encodeURIComponent(code)}/postcodes`, form, {
      headers: { 'Content-Type': undefined },
    })
    return response.data
  },
}

// Shape of an api_access_requests row as returned by the admin list endpoint.
export interface AccessRequest {
  id: string
  email: string
  first_name: string
  last_name: string
  company: string
  use_case: string | null
  request_type: 'documentation' | 'access'
  status: 'pending' | 'approved' | 'rejected'
  code_expires_at: string | null
  code_used_at: string | null
  approved_by: string | null
  approved_at: string | null
  rejected_by: string | null
  rejected_at: string | null
  rejection_reason: string | null
  verified_at: string | null
  verification_token_expires_at: string | null
  totp_enrolled_at: string | null
  last_seen_at: string | null
  created_at: string
  updated_at: string
}

export default apiClient
